/**
 * Detajet e asetit — `/assets/:id`.
 *
 * Faqe, jo dialog. Një pajisje ka gjashtë familje të dhënash (identiteti,
 * caktimi, historiku, servisimet, dokumentet, ndalesat) dhe secila ka rreshtat
 * e vet; të gjitha brenda një modali do të thoshin skroll brenda skrolli.
 *
 * Koka mban atë që lexohet me një shikim — kodi, statusi, kush e mban, sa vlen
 * dhe deri kur ka garancion. Tabet mbajnë hollësitë. Shiriti djathtas mban
 * caktimin aktual dhe tri ngjarjet e fundit, sepse ato janë pyetja e parë e
 * kujtdo që e hap faqen: «te kush është tani dhe çka i ndodhi së fundmi?».
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, IconButton, Tabs, Tab, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, Alert, CircularProgress,
  Breadcrumbs, Avatar, Chip, Menu, MenuItem, Tooltip, Grid,
} from '@mui/material';
import ArrowBackIcon         from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon      from '@mui/icons-material/EditOutlined';
import MoreHorizIcon         from '@mui/icons-material/MoreHoriz';
import PrintOutlinedIcon     from '@mui/icons-material/PrintOutlined';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';
import AssignmentIndIcon     from '@mui/icons-material/AssignmentInd';
import HistoryIcon           from '@mui/icons-material/History';
import BuildOutlinedIcon     from '@mui/icons-material/BuildOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaymentsOutlinedIcon  from '@mui/icons-material/PaymentsOutlined';
import AddIcon               from '@mui/icons-material/Add';
import DeleteOutlineIcon     from '@mui/icons-material/DeleteOutline';
import DownloadIcon          from '@mui/icons-material/Download';
import AssignmentReturnIcon  from '@mui/icons-material/AssignmentReturn';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UploadFileIcon        from '@mui/icons-material/UploadFile';

import { ReportPage }  from '../report/ReportKit';
import { FORM_DENSITY } from './FormKit';
import StatusChip      from '../common/StatusChip';
import Toast           from '../actions/Toasts';
import usePermissions  from '../../hooks/usePermissions';
import useConfirm      from '../../hooks/useConfirm';
import assetsService   from '../../services/assetsService';
import apiService      from '../../services/apiServices';
import normalizeWorker from '../../services/workerNormalizer';
import AuthImage       from './AuthImage';
import AssignAssetDialog from './AssignAssetDialog';
import ReturnAssetDialog from './ReturnAssetDialog';
import ServiceDialog     from './ServiceDialog';
import IncidentDialog    from './IncidentDialog';
import {
  assetTitle, categoryLabel, categoryMeta, statusChip, statusLabel,
  conditionChip, conditionLabel, dateText, money, identifierLabel, filledSpecs,
  paymentLabel, serviceChip, serviceLabel, incidentLabel, warrantyState,
  canAssign, canReturn,
} from '../../config/assets';

const todayISO = () => new Date().toISOString().slice(0, 10);

/** Etiketë e vogël + vlerë — njësia e vetme e leximit në gjithë faqen. */
function Field({ label, children, minWidth = 150 }) {
  return (
    <Box sx={{ minWidth }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: '0.85rem', fontWeight: 600, mt: 0.2 }}>{children ?? '—'}</Box>
    </Box>
  );
}

/** Rresht "etiketë : vlerë" për kartat e informacionit. */
function Row({ label, value }) {
  return (
    <Stack direction="row" sx={{ py: 0.35, gap: 2, alignItems: 'baseline' }}>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', minWidth: 140 }}>{label}</Typography>
      <Box sx={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: 0 }}>{value || '—'}</Box>
    </Stack>
  );
}

function Card({ title, icon, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 1, minHeight: 26 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {icon}
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{title}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

const TABS = [
  { key: 'info',      label: 'Informacioni', Icon: InfoOutlinedIcon },
  { key: 'assign',    label: 'Caktimi',      Icon: AssignmentIndIcon },
  { key: 'history',   label: 'Historiku',    Icon: HistoryIcon },
  { key: 'services',  label: 'Servisimet',   Icon: BuildOutlinedIcon },
  { key: 'documents', label: 'Dokumentet',   Icon: DescriptionOutlinedIcon },
  { key: 'deductions',label: 'Ndalesat',     Icon: PaymentsOutlinedIcon },
];

export default function AssetDetailPage() {
  const { id }  = useParams();
  const history = useHistory();

  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [tab,     setTab]     = useState(0);
  const [workers, setWorkers] = useState([]);
  const [menuEl,  setMenuEl]  = useState(null);
  const [snack,   setSnack]   = useState({ open: false, msg: '', sev: 'success' });

  const [assignOpen,   setAssignOpen]   = useState(false);
  const [returnOpen,   setReturnOpen]   = useState(false);
  const [serviceOpen,  setServiceOpen]  = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);

  const { can } = usePermissions();
  const canEdit   = can('assets.edit') || can('assets.manage');
  const canManage = can('assets.manage');
  const [confirm, confirmUI] = useConfirm();
  const say = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setDetail(await assetsService.detail(id));
    } catch (e) {
      setError(e.message || 'Aseti nuk u ngarkua.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    apiService().get('workers')
      .then((r) => {
        const raw = r?.data || r;
        if (alive && Array.isArray(raw)) setWorkers(raw.map((w, i) => normalizeWorker(w, i)));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const asset       = detail?.asset || null;
  const assignments = detail?.assignments || [];
  const events      = detail?.events || [];
  const accessories = detail?.accessories || [];
  const services    = detail?.services || [];
  const incidents   = detail?.incidents || [];
  const files       = detail?.files || [];
  const openAssignment = useMemo(() => assignments.find((a) => a.open) || null, [assignments]);
  const today = todayISO();

  // Ndalesat nuk kanë tabelë të vetën: ato janë rreshtat e caktimeve dhe të
  // incidenteve që prodhuan një rregullim page. Të mbledhura këtu, tabi tregon
  // pikërisht atë që dikush do të kërkonte — sa i është ndaluar kujt e pse.
  const deductions = useMemo(() => [
    ...assignments.filter((a) => a.damageCost || a.deductionId).map((a) => ({
      key: `a${a.id}`, date: a.returnedOn, worker: a.workerName,
      reason: a.damageNote || 'Dëmtim i konstatuar në kthim',
      amount: a.damageCost, deductionId: a.deductionId,
    })),
    ...incidents.filter((i) => i.cost || i.deductionId).map((i) => ({
      key: `i${i.id}`, date: i.incidentDate, worker: i.workerName,
      reason: `${i.kindLabel} — ${i.description}`,
      amount: i.cost, deductionId: i.deductionId,
    })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date))), [assignments, incidents]);

  async function doAssign(body) {
    await assetsService.assign(asset.id, body);
    setAssignOpen(false);
    say('Pajisja u dorëzua.');
    load();
  }

  async function doReturn(body) {
    const res = await assetsService.ret(asset.id, body);
    setReturnOpen(false);
    say(res.deductionId
      ? `Kthimi u regjistrua. Ndalesa #${res.deductionId} pret aprovim.`
      : 'Kthimi u regjistrua.');
    load();
  }

  async function addService(body) {
    await assetsService.addService(asset.id, body);
    setServiceOpen(false);
    say('Servisimi u regjistrua.');
    load();
  }

  async function addIncident(body) {
    const res = await assetsService.addIncident(asset.id, body);
    setIncidentOpen(false);
    say(res.deductionId
      ? `Incidenti u regjistrua. Ndalesa #${res.deductionId} pret aprovim.`
      : 'Incidenti u regjistrua.');
    load();
  }

  async function uploadFile(e, kind) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await assetsService.uploadFile(asset.id, file, { kind });
      say('Skedari u ngarkua.');
      load();
    } catch (err) {
      say(err.message || 'Ngarkimi dështoi.', 'error');
    }
  }

  async function removeFile(f) {
    if (!await confirm({ title: 'Fshi skedarin', message: f.name, confirmLabel: 'Fshi' })) return;
    try {
      await assetsService.deleteFile(f.id);
      say('Skedari u fshi.');
      load();
    } catch (err) { say(err.message || 'Fshirja dështoi.', 'error'); }
  }

  if (loading) {
    return <ReportPage><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></ReportPage>;
  }
  if (error || !asset) {
    return <ReportPage><Alert severity="error" sx={{ m: 2 }}>{error || 'Aseti nuk u gjet.'}</Alert></ReportPage>;
  }

  const Cat = categoryMeta(asset.category).Icon;
  const warranty = warrantyState(asset.warrantyUntil, today);
  const photos = files.filter((f) => f.kind === 'photo');
  const docs   = files.filter((f) => f.kind !== 'photo');

  return (
    <ReportPage sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...FORM_DENSITY }}>

      <Breadcrumbs sx={{ fontSize: '0.75rem' }}>
        <Link to="/assets" style={{ color: 'inherit', textDecoration: 'none' }}>Asetet</Link>
        <Typography sx={{ fontSize: '0.75rem' }} color="text.primary">{asset.assetCode}</Typography>
      </Breadcrumbs>

      {/* Koka */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => history.push('/assets')} aria-label="Kthehu te lista">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Detajet e asetit</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<PrintOutlinedIcon />}
            onClick={() => window.print()} sx={{ textTransform: 'none' }}>
            Printo
          </Button>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
              onClick={() => history.push(`/assets/${asset.id}/edit`)} sx={{ textTransform: 'none' }}>
              Redakto
            </Button>
          )}
          <Button size="small" variant="contained" endIcon={<MoreHorizIcon />}
            onClick={(e) => setMenuEl(e.currentTarget)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Më shumë
          </Button>
          <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
            {canManage && canAssign(asset) && (
              <MenuItem onClick={() => { setMenuEl(null); setAssignOpen(true); }}>Dorëzo te punonjësi</MenuItem>
            )}
            {canManage && canReturn(asset) && (
              <MenuItem onClick={() => { setMenuEl(null); setReturnOpen(true); }}>Regjistro kthimin</MenuItem>
            )}
            {canManage && <MenuItem onClick={() => { setMenuEl(null); setServiceOpen(true); }}>Regjistro servis</MenuItem>}
            {canManage && <MenuItem onClick={() => { setMenuEl(null); setIncidentOpen(true); }}>Raporto dëmtim</MenuItem>}
            {canManage && canAssign(asset) && (
              <MenuItem onClick={() => { setMenuEl(null); history.push(`/assets/handover/new?asset=${asset.id}`); }}>
                Procesverbal dorëzimi
              </MenuItem>
            )}
            {canManage && canReturn(asset) && (
              <MenuItem onClick={() => { setMenuEl(null); history.push(`/assets/handover/new?kind=return&asset=${asset.id}`); }}>
                Procesverbal kthimi
              </MenuItem>
            )}
          </Menu>
        </Stack>
      </Stack>

      {/* Kartela e identitetit */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={2}>
              <AuthImage fileId={photos[0]?.id} alt={asset.name}
                sx={{ width: 116, height: 86, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={asset.assetCode}
                    sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem' }} />
                  <StatusChip status={statusChip(asset.status)} label={statusLabel(asset.status)} size="sm" />
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{asset.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {identifierLabel(asset.category)}: {asset.serialNumber || '—'}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Field label="Kategoria" minWidth={90}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Cat sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <span>{categoryLabel(asset.category)}</span>
                    </Stack>
                  </Field>
                  <Field label="Marka" minWidth={80}>{asset.brand}</Field>
                  <Field label="Modeli" minWidth={110}>{asset.model}</Field>
                  <Field label="Kodi intern" minWidth={100}>{asset.internalCode}</Field>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Stack spacing={1}>
              <Field label="Data e blerjes">{dateText(asset.purchaseDate)}</Field>
              <Field label="Vlera e blerjes">{money(asset.purchaseValue, asset.currency)}</Field>
              <Field label="Furnitori">{asset.supplier}</Field>
              <Field label="Garancioni deri më">
                {/* Garancioni i skaduar nuk fshihet — vetëm hiqet theksi, që të
                    lexohet si "ka ekzistuar dhe ka mbaruar". */}
                <Typography component="span" sx={{
                  fontSize: '0.85rem', fontWeight: 600,
                  color: warranty === 'expired' ? 'text.disabled' : 'text.primary',
                  textDecoration: warranty === 'expired' ? 'line-through' : 'none',
                }}>
                  {dateText(asset.warrantyUntil)}
                </Typography>
              </Field>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Stack spacing={1}>
              <Field label="Lokacioni">{asset.location}</Field>
              <Field label="Gjendja">
                <StatusChip status={conditionChip(asset.condition)} label={conditionLabel(asset.condition)} size="sm" />
              </Field>
              <Field label="Departamenti">{asset.departmentName}</Field>
              <Field label="Përgjegjësi">{asset.responsibleName}</Field>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={1.25}>
        {/* ── Tabet ── */}
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider', minHeight: 42,
                    '& .MuiTab-root': { fontWeight: 600, fontSize: '0.8rem', textTransform: 'none', minHeight: 42 } }}>
              {TABS.map((t) => (
                <Tab key={t.key} label={t.label} icon={<t.Icon sx={{ fontSize: 16 }} />} iconPosition="start" />
              ))}
            </Tabs>

            <Box sx={{ p: 1.75 }}>
              {/* ── 0. Informacioni ── */}
              {tab === 0 && (
                <Stack spacing={1.25}>
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} md={6}>
                      <Card title="Informacioni i asetit" icon={<InfoOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />}>
                        <Row label="Kategoria" value={categoryLabel(asset.category)} />
                        {asset.subcategory && <Row label="Nënkategoria" value={asset.subcategory} />}
                        <Row label="Marka / Modeli" value={[asset.brand, asset.model].filter(Boolean).join(' ')} />
                        <Row label={identifierLabel(asset.category)} value={asset.serialNumber} />
                        {/* Specifikimet vijnë sipas kategorisë: një automjet nuk
                            pyetet për RAM, dhe një karrige as për CPU. */}
                        {/* Lexohen TË GJITHA specifikimet e ruajtura, jo vetëm
                            ato që sugjeron kategoria — përndryshe një fushë e
                            shtuar nga përdoruesi («Numri i shasisë») do të ruhej
                            dhe s'do të shihej askund. */}
                        {filledSpecs(asset.specs).map((sp) => (
                          <Row key={sp.key} label={sp.label} value={sp.value} />
                        ))}
                        <Row label="Përshkrimi" value={asset.description} />
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card title="Të dhëna financiare" icon={<PaymentsOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />}>
                        <Row label="Vlera e blerjes" value={money(asset.purchaseValue, asset.currency)} />
                        <Row label="Monedha" value={asset.currency} />
                        <Row label="Furnitori" value={asset.supplier} />
                        <Row label="Fatura / Nr. dokumentit" value={asset.invoiceNo} />
                        <Row label="Data e blerjes" value={dateText(asset.purchaseDate)} />
                        <Row label="Metoda e pagesës" value={paymentLabel(asset.paymentMethod)} />
                        <Row label="Amortizimi (vjet)" value={asset.amortizationYears ? `${asset.amortizationYears} vite` : ''} />
                        {/* Llogaritur nga serveri — jo kolonë e dytë që mund të
                            binte ndesh me vlerën e blerjes. */}
                        <Row label="Vlera e mbetur" value={money(asset.residualValue, asset.currency)} />
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Tri veprimet e ciklit rrinë KËTU, jo vetëm te menyja "Më
                      shumë": secila kartë thotë çka ka ndodhur ("Nuk ka
                      servisime") para se të ftojë në veprim. Një buton pa atë
                      fjali të detyron të hapësh tabin për ta ditur. */}
                  <Grid container spacing={1.25}>
                    {[
                      { key: 'svc', title: 'Servisimet', Icon: BuildOutlinedIcon,
                        empty: 'Nuk ka servisime të regjistruara.',
                        count: services.length,
                        summary: services[0] && `I fundit: ${dateText(services[0].serviceDate)} · ${services[0].provider || services[0].description}`,
                        action: 'Regjistro servis', on: () => setServiceOpen(true), show: canManage },
                      { key: 'inc', title: 'Dëmtime / Incidente', Icon: ReportProblemOutlinedIcon,
                        empty: 'Nuk ka dëmtime të regjistruara.',
                        count: incidents.length,
                        summary: incidents[0] && `I fundit: ${dateText(incidents[0].incidentDate)} · ${incidents[0].kindLabel}`,
                        action: 'Raporto dëmtim', on: () => setIncidentOpen(true), show: canManage },
                      { key: 'ret', title: 'Kthimi i asetit', Icon: AssignmentReturnIcon,
                        empty: openAssignment ? 'Aseti nuk është kthyer ende.' : 'Aseti nuk është te askush.',
                        count: assignments.filter((a) => !a.open).length,
                        summary: openAssignment && `Te ${openAssignment.workerName || 'punonjësi'} prej ${dateText(openAssignment.assignedOn)}`,
                        action: 'Regjistro kthim', on: () => setReturnOpen(true),
                        show: canManage && !!openAssignment },
                    ].map((c) => (
                      <Grid item xs={12} md={4} key={c.key}>
                        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75, height: '100%',
                                                        display: 'flex', flexDirection: 'column' }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                            <c.Icon sx={{ fontSize: 17, color: 'text.secondary' }} />
                            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{c.title}</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 1.25 }}>
                            {c.summary || c.empty}
                          </Typography>
                          {c.show && (
                            <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={c.on}
                              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                              {c.action}
                            </Button>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Card title={`Aksesorë & Shtesë (${accessories.length})`}
                    icon={<AddIcon sx={{ fontSize: 17, color: 'primary.main' }} />}>
                    {accessories.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Pa aksesorë të regjistruar.</Typography>
                    ) : (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {accessories.map((a) => (
                          <Paper key={a.id} variant="outlined"
                            sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{a.name}</Typography>
                              {(a.serialNumber || a.description) && (
                                <Typography variant="caption" color="text.secondary">
                                  {[a.description, a.serialNumber].filter(Boolean).join(' · ')}
                                </Typography>
                              )}
                            </Box>
                            <StatusChip status={a.included ? 'approved' : 'reversed'}
                              label={a.included ? 'Po' : 'Jo'} size="sm" />
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Card>
                </Stack>
              )}

              {/* ── 1. Caktimi ── */}
              {tab === 1 && (
                <>
                  {assignments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Pajisja nuk i është dorëzuar askujt deri tani.
                    </Typography>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Dorëzuar</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Kthyer</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Gjendja dhënie → kthim</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Pajisjet shtesë</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Procesverbal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assignments.map((a) => (
                            <TableRow key={a.id}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {a.workerName || <Typography component="span" variant="body2" color="text.disabled">Punëtor i fshirë</Typography>}
                              </TableCell>
                              <TableCell>{dateText(a.assignedOn)}</TableCell>
                              <TableCell>
                                {a.open
                                  ? <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'info.onSoft' }}>Ende te punonjësi</Typography>
                                  : dateText(a.returnedOn)}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <StatusChip status={conditionChip(a.condition)} label={conditionLabel(a.condition)} size="sm" />
                                  {!a.open && a.returnCondition && (
                                    <>
                                      <Typography component="span" color="text.disabled">→</Typography>
                                      <StatusChip status={conditionChip(a.returnCondition)} label={conditionLabel(a.returnCondition)} size="sm" />
                                    </>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>{a.accessories || '—'}</TableCell>
                              <TableCell>{a.documentName || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </>
              )}

              {/* ── 2. Historiku ── */}
              {tab === 2 && (
                events.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Pa ngjarje të regjistruara.</Typography>
                ) : (
                  <Stack>
                    {events.map((e) => (
                      <Stack key={e.id} direction="row" spacing={1.5}
                        sx={{ py: 0.9, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 82,
                                          fontVariantNumeric: 'tabular-nums' }}>
                          {dateText(e.eventOn)}
                        </Typography>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                            {e.actionLabel}
                            {e.workerName ? <Typography component="span" sx={{ fontWeight: 500 }}> · {e.workerName}</Typography> : null}
                          </Typography>
                          {e.fromStatus && e.toStatus && e.fromStatus !== e.toStatus && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
                              <StatusChip status={statusChip(e.fromStatus)} label={statusLabel(e.fromStatus)} size="sm" />
                              <Typography component="span" color="text.disabled">→</Typography>
                              <StatusChip status={statusChip(e.toStatus)} label={statusLabel(e.toStatus)} size="sm" />
                            </Stack>
                          )}
                          {e.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{e.note}</Typography>}
                        </Box>
                        {e.actorEmail && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                            {e.actorEmail}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )
              )}

              {/* ── 3. Servisimet ── */}
              {tab === 3 && (
                <Stack spacing={1.25}>
                  {canManage && (
                    <Box>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />}
                        onClick={() => setServiceOpen(true)} sx={{ textTransform: 'none' }}>
                        Regjistro servis
                      </Button>
                    </Box>
                  )}
                  {services.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nuk ka servisime të regjistruara.</Typography>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Ofruesi</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Përshkrimi</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Servisi i radhës</TableCell>
                            <TableCell className="num" sx={{ fontWeight: 700 }}>Kosto</TableCell>
                            {canManage && <TableCell />}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {services.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell>{dateText(s.serviceDate)}</TableCell>
                              <TableCell>{s.provider || '—'}</TableCell>
                              <TableCell>{s.description || '—'}</TableCell>
                              <TableCell><StatusChip status={serviceChip(s.status)} label={serviceLabel(s.status)} size="sm" /></TableCell>
                              <TableCell>{dateText(s.nextServiceDate)}</TableCell>
                              <TableCell className="num">{money(s.cost, s.currency)}</TableCell>
                              {canManage && (
                                <TableCell align="right">
                                  <IconButton size="small" color="error"
                                    onClick={async () => {
                                      if (!await confirm({ title: 'Fshi servisimin', message: s.description || s.provider, confirmLabel: 'Fshi' })) return;
                                      await assetsService.deleteService(s.id);
                                      say('Servisimi u fshi.'); load();
                                    }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  <Divider />
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                      Dëmtime / Incidente ({incidents.length})
                    </Typography>
                    {canManage && (
                      <Button size="small" variant="outlined" color="warning" startIcon={<ReportProblemOutlinedIcon />}
                        onClick={() => setIncidentOpen(true)} sx={{ textTransform: 'none' }}>
                        Raporto dëmtim
                      </Button>
                    )}
                  </Stack>
                  {incidents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Nuk ka dëmtime të regjistruara.</Typography>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Lloji</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Përshkrimi</TableCell>
                            <TableCell className="num" sx={{ fontWeight: 700 }}>Kosto</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Ndalesa</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {incidents.map((i) => (
                            <TableRow key={i.id}>
                              <TableCell>{dateText(i.incidentDate)}</TableCell>
                              <TableCell>{incidentLabel(i.kind)}</TableCell>
                              <TableCell>{i.workerName || '—'}</TableCell>
                              <TableCell>{i.description}</TableCell>
                              <TableCell className="num">{money(i.cost, i.currency)}</TableCell>
                              <TableCell>{i.deductionId ? `#${i.deductionId}` : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Stack>
              )}

              {/* ── 4. Dokumentet ── */}
              {tab === 4 && (
                <Stack spacing={1.25}>
                  {canEdit && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}
                        sx={{ textTransform: 'none' }}>
                        Ngarko dokument
                        <input type="file" hidden onChange={(e) => uploadFile(e, 'document')} />
                      </Button>
                      <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}
                        sx={{ textTransform: 'none' }}>
                        Ngarko foto
                        <input type="file" hidden accept="image/*" onChange={(e) => uploadFile(e, 'photo')} />
                      </Button>
                    </Stack>
                  )}

                  {photos.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {photos.map((f) => (
                        <Box key={f.id} sx={{ position: 'relative' }}>
                          <AuthImage fileId={f.id} alt={f.name} sx={{ width: 128, height: 96 }} />
                          {canEdit && (
                            <IconButton size="small" onClick={() => removeFile(f)}
                              sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper' }}>
                              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {docs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Pa dokumente të ngarkuara.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Emri</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Lloji</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Ngarkuar nga</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {docs.map((f) => (
                          <TableRow key={f.id}>
                            <TableCell sx={{ fontWeight: 600 }}>{f.name}</TableCell>
                            <TableCell>{f.kind}</TableCell>
                            <TableCell>{f.uploadedByName || '—'}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Shkarko" arrow>
                                <IconButton size="small" component="a" href={assetsService.fileUrl(f.id)}>
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {canEdit && (
                                <IconButton size="small" color="error" onClick={() => removeFile(f)}>
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Stack>
              )}

              {/* ── 5. Ndalesat ── */}
              {tab === 5 && (
                deductions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Kjo pajisje nuk ka prodhuar asnjë ndalesë.
                  </Typography>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Ndalesat krijohen si <strong>draft</strong> te Rregullimet e pagës dhe kalojnë nëpër
                      të njëjtin aprovim si çdo ndalesë tjetër.
                    </Alert>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Punonjësi</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Arsyeja</TableCell>
                          <TableCell className="num" sx={{ fontWeight: 700 }}>Shuma</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Rregullimi</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deductions.map((d) => (
                          <TableRow key={d.key}>
                            <TableCell>{dateText(d.date)}</TableCell>
                            <TableCell>{d.worker || '—'}</TableCell>
                            <TableCell>{d.reason}</TableCell>
                            <TableCell className="num">{money(d.amount, asset.currency)}</TableCell>
                            <TableCell>
                              {d.deductionId
                                ? <Link to="/payroll-adjustments" style={{ color: 'inherit' }}>#{d.deductionId}</Link>
                                : <Typography component="span" variant="body2" color="text.disabled">pa ndalesë</Typography>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )
              )}
            </Box>
          </Paper>
        </Grid>

        {/* ── Shiriti djathtas ── */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={1.25}>
            <Card title="Caktuar te" icon={<AssignmentIndIcon sx={{ fontSize: 17, color: 'primary.main' }} />}
              action={canManage && (openAssignment
                ? <Button size="small" startIcon={<AssignmentReturnIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setReturnOpen(true)} sx={{ textTransform: 'none' }}>Kthimi</Button>
                : canAssign(asset) && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setAssignOpen(true)} sx={{ textTransform: 'none' }}>Dorëzo</Button>
                  ))}>
              {openAssignment ? (
                <>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                    <Avatar sx={{ width: 42, height: 42 }}>
                      {(openAssignment.workerName || '?').slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                        {openAssignment.workerName || 'Punëtor i fshirë'}
                      </Typography>
                      {asset.holder?.position && (
                        <Typography variant="caption" color="text.secondary">{asset.holder.position}</Typography>
                      )}
                      {asset.holder?.department && (
                        <Box><Chip size="small" label={asset.holder.department}
                          sx={{ height: 20, fontSize: '0.65rem', mt: 0.4 }} /></Box>
                      )}
                    </Box>
                  </Stack>
                  <Row label="Data e dorëzimit" value={dateText(openAssignment.assignedOn)} />
                  <Row label="Gjendja në dorëzim" value={conditionLabel(openAssignment.condition)} />
                  <Row label="Pajisjet shtesë" value={openAssignment.accessories} />
                  <Row label="Dokumenti" value={openAssignment.documentName} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Pajisja nuk është te askush.
                </Typography>
              )}
            </Card>

            <Card title="Historiku i fundit" icon={<HistoryIcon sx={{ fontSize: 17, color: 'primary.main' }} />}
              action={<Button size="small" onClick={() => setTab(2)} sx={{ textTransform: 'none' }}>Shiko të gjithë</Button>}>
              {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Pa ngjarje.</Typography>
              ) : (
                <Stack spacing={1}>
                  {events.slice(0, 4).map((e) => (
                    <Stack key={e.id} direction="row" spacing={1} alignItems="flex-start">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.7, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{e.actionLabel}</Typography>
                        {e.note && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                            {e.note}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                        {dateText(e.eventOn)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <AssignAssetDialog open={assignOpen} asset={asset} workers={workers} today={today}
        onClose={() => setAssignOpen(false)} onAssign={doAssign} />
      <ReturnAssetDialog open={returnOpen} asset={asset} assignment={openAssignment} today={today}
        onClose={() => setReturnOpen(false)} onReturn={doReturn} />
      <ServiceDialog open={serviceOpen} asset={asset} today={today}
        onClose={() => setServiceOpen(false)} onSave={addService} />
      <IncidentDialog open={incidentOpen} asset={asset} assignment={openAssignment} today={today}
        onClose={() => setIncidentOpen(false)} onSave={addIncident} />

      {confirmUI}
      <Toast open={snack.open} message={snack.msg} severity={snack.sev}
        onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </ReportPage>
  );
}
